import {validateFilenameForUrl} from '@/helpers/shadcn/utils'
import {FILE} from '@/utils/constants/commonConstants'
import ErrorOutlineOutlinedIcon from '@mui/icons-material/ErrorOutlineOutlined'
import PhotoSizeSelectActualIconOutlined from '@mui/icons-material/PhotoSizeSelectActualOutlined'
import {useRef, useState, useEffect} from 'react'
import Label from '@/components/atoms/Label/Label'
import Icon from '@/components/atoms/Icon/Icon'
import Button from '@/components/atoms/Button/Button'

const ImagePicker = ({
  onImageSelect = () => {},
  value = null,
  label = 'Reward Image*',
  allowedMimeTypes,
  invalidFormatsMessage,
  disableDelete = false,
  disabled = false,
  id,
  displaySingleImageUpload,
}) => {
  const [image, setImage] = useState(value)
  const [error, setError] = useState('')
  const inputRef = useRef(null)

  // allow override of allowed mime types; default to global constant
  const validTypes = allowedMimeTypes || FILE.ALLOWED_IMAGE_TYPES
  const {maxIndividualImageSize = '70KB'} = {}
  // keep local state in sync with parent value (do not call onImageSelect here to avoid loops)
  useEffect(() => {
    if (!value) {
      setImage(null)
      setError('')
    } else {
      setImage(value)
    }
  }, [value])

  // Helper to get natural dimensions of an image file
  const getImageDimensions = file =>
    new Promise((resolve, reject) => {
      try {
        const url = URL.createObjectURL(file)
        const img = new Image()
        img.onload = () => {
          resolve({ width: img.naturalWidth, height: img.naturalHeight, url })
        }
        img.onerror = err => {
          URL.revokeObjectURL(url)
          reject(err)
        }
        img.src = url
      } catch (e) {
        reject(e)
      }
    })

  // Parse size strings like "70KB" or "1MB" to bytes for validation
  const parseSizeToBytes = sizeStr => {
    const num = Number(sizeStr?.match(/\d+/)?.[0])
    if (!num) return Infinity
    const upper = String(sizeStr).toUpperCase()
    const isKB = upper.includes('KB')
    return num * (isKB ? 1024 : FILE.SIZE_BYTES)
  }

  // Helper functions to reduce complexity
  const validateFileType = (file) => {
    if (!validTypes.includes(file.type)) {
      return { isValid: false, message: invalidFormatsMessage || 'File format is not valid. Supported Formats: PNG|JPG' }
    }
    return { isValid: true }
  }

  const validateFilename = (filename) => {
    const validation = validateFilenameForUrl(filename)
    if (!validation.isValid) {
      return { isValid: false, message: validation.message }
    }
    return { isValid: true }
  }

  const validateFileSize = (fileSize) => {
    if (fileSize >= parseSizeToBytes(maxIndividualImageSize)) {
      return { isValid: false, message: `${displaySingleImageUpload ? 'Image' : 'Each image'} must be less than or equal to ${maxIndividualImageSize}.` }
    }
    return { isValid: true }
  }

  const validateAspectRatio = (width, height) => {
    const ratio = width / height
    const target = 4 / 3
    const tolerance = 0.02
    return Math.abs(ratio - target) <= target * tolerance
  }

  const handleFileError = (message, url = null) => {
    if (url) URL.revokeObjectURL(url)
    setImage(null)
    setError(message)
    onImageSelect(null)
  }

  const handleFile = async file => {
    if (!file) return

    // Validate file type
    const typeValidation = validateFileType(file)
    if (!typeValidation.isValid) {
      handleFileError(typeValidation.message)
      return
    }

    // Validate filename
    const filenameValidation = validateFilename(file.name)
    if (!filenameValidation.isValid) {
      handleFileError(filenameValidation.message)
      return
    }

    // Validate file size
    const sizeValidation = validateFileSize(file.size)
    if (!sizeValidation.isValid) {
      setError(sizeValidation.message)
      return
    }

    setError('')

    // Validate aspect ratio
    try {
      const { width, height, url } = await getImageDimensions(file)
      
      if (!validateAspectRatio(width, height)) {
        handleFileError('Image must have a 4:3 aspect ratio (e.g., 1024x768, 800x600).', url)
        return
      }

      setImage(url)
      onImageSelect(file)
    } catch (_) {
      handleFileError('Unable to read the image. Please try another file.')
    }
  }

  const handleImageChange = e => {
    const file = e.target.files[0]
    handleFile(file)
  }

  const handleDrop = e => {
    e.preventDefault()
    const file = e.dataTransfer.files[0]
    handleFile(file)
  }
  const removeImage = () => {
    setImage(null)
    setError('')
    onImageSelect(null)
  }

  // Computed values to reduce ternary operator complexity
  const rootClassName = `w-full ${displaySingleImageUpload ? 'rounded-lg border border-gray-600 pt-4 pb-4' : ''}`
  const rootTestId = id ? `${id}` : 'image-picker-root'
  const tooltipText = displaySingleImageUpload 
    ? "PNG | JPG | 500 × 500 px | RGB Color | 4:3 Ratio | Max 70 KB | Without background"
    : "PNG | JPG | RGB Color | 4:3 ratio | Max 70 KB"
  const containerClassName = `relative rounded-lg ${!displaySingleImageUpload ? 'border border-gray-600 pl-8 mt-2' : ''} py-2 px-6`
  const containerTestId = id ? `${id}-container` : 'image-picker-container'
  const dropAreaTestId = id ? `${id}-drop-area` : 'image-picker-drop-area'
  const fileInputTestId = id ? `${id}-file-input` : 'file-input'
  const uploadBtnId = id ? `${id}-upload-btn` : 'image-picker-upload-btn'
  const previewTestId = id ? `${id}-preview` : 'image-preview'
  const deleteBtnTestId = id ? `${id}-delete-btn` : 'delete-image-btn'
  const errorTestId = id ? `${id}-error` : 'image-picker-error'
  const labelClassName = `text-[12px] text-[#efefef] ${displaySingleImageUpload ? 'ml-6' : ''}`

  return (
    <div className={rootClassName} data-testid={rootTestId} id={id}>
      <Label
        className={labelClassName}
        label={label}
        showLabelIcon
        tooltipText={tooltipText}
      />

      <div className={containerClassName} data-testid={containerTestId}>
        {/* Drag handle indicator - vertically centered on the left */}
        {!displaySingleImageUpload && <Icon
          icon={"drag_indicator"}
          size="small"
          isDark
          className="absolute left-2 top-1/2 -translate-y-1/2 z-10"
          aria-hidden="true"
        />}
        {!image ? (
          <div
            className={`relative rounded-xl border bg-gray-800 text-center ${
              error ? 'border-red-500' : 'border-dashed border-gray-400'
            } ${disabled ? 'pointer-events-none opacity-50' : 'cursor-pointer'}`}
            onDrop={handleDrop}
            onDragOver={e => e.preventDefault()}
            data-testid={dropAreaTestId}
          >
            <label className="block h-full w-full cursor-pointer">
              <input
                type="file"
                accept={validTypes.join(', ')}
                onChange={handleImageChange}
                className="hidden"
                ref={inputRef}
                data-testid={fileInputTestId}
              />
              <div
                className={`flex aspect-[4/3] h-full min-h-[160px] w-full flex-col items-center justify-center p-6`}
              >
                <PhotoSizeSelectActualIconOutlined fontSize="large" />
                <p className="text-[12px] text-gray-50 pt-2">Drag and drop your file here</p>
                <p className="text-[12px] text-gray-50 pb-4">(only {validTypes.map(t => t.replace('image/', '.')).join(', ').replace('jpeg', 'jpg')})</p>
                <Button
                  type="button"
                  handleButton={() => inputRef.current?.click()}
                  id={uploadBtnId}
                  uiSchema={{
                    label: 'Upload file',
                    size: 'medium',
                    isDark: true,
                    variant: 'secondary',
                  }}
                >
                  <Icon icon="file_upload" size="medium" isDark /> Upload file
                </Button>
              </div>
            </label>
          </div>
        ) : (
          <div className={`relative overflow-hidden rounded-xl ${disabled ? 'opacity-90' : ''}`} data-testid={previewTestId}>
            <div className="aspect-[4/3] min-h-[160px] w-full">
              <img src={image} alt="Selected" className="h-full w-full object-cover" />
            </div>
            {!disableDelete && !disabled && (
              <button
                onClick={removeImage}
                className="absolute bottom-3 right-3 rounded-[.25rem] border border-gray-600 bg-gray-850 p-2 text-[#66A5E7]"
                title="Remove image"
                data-testid={deleteBtnTestId}
              >
                <Icon icon="delete" size="medium" isDark />
              </button>
            )}
          </div>
        )}
        {error && (
          <p className="mt-2 flex items-center gap-1 text-xs text-red-500" data-testid={errorTestId}>
            <ErrorOutlineOutlinedIcon fontSize="small" /> {error}
          </p>
        )}
      </div>
    </div>
  )
}

export default ImagePicker
